import { useMutation } from '@tanstack/react-query'
import instance from '../api'
import axios from 'axios'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import {
  ChallengeFailedError,
  GetTurnstileToken,
  TURNSTILE_FIELD,
  takeChallengeToken,
} from '../turnstile/challenge'

export type CheckoutPaymentMethod = 'card' | 'bank_transfer'

interface CheckoutRequest {
  pricing_plan_id: number
  first_name: string
  last_name: string
  username: string
  email: string
  company_name: string
  company_email: string
  company_tax_id: string
  country: string
  state: string
  city: string
  postal_code: string
  address_line_1: string
  address_line_2?: string
  discount_code?: string
  // Must match PaymentProvider on the backend: anything other than the exact
  // string 'bank_transfer' falls through to the Stripe branch.
  payment_method?: CheckoutPaymentMethod
}

interface CheckoutMutation extends CheckoutRequest {
  getToken: GetTurnstileToken
}

interface CardCheckoutResponse {
  checkout_url: string
  session_id: string
}

/** Bank transfer is fully off-Stripe: the account is provisioned immediately
 *  and the bank details are emailed, so there is nowhere to redirect to. */
interface BankTransferCheckoutResponse {
  status: 'pending'
  subscription_id: number
}

export type CheckoutResponse =
  | CardCheckoutResponse
  | BankTransferCheckoutResponse

export const isCardCheckout = (
  response: CheckoutResponse,
): response is CardCheckoutResponse =>
  Boolean((response as CardCheckoutResponse)?.checkout_url)

export const createCheckout = async ({
  getToken,
  ...data
}: CheckoutMutation): Promise<CheckoutResponse> => {
  const token = await takeChallengeToken(getToken)
  const response = await instance.post('/payment/checkouts/checkout/', {
    ...data,
    ...(token ? { [TURNSTILE_FIELD]: token } : {}),
  })
  return response.data.data
}

const useCreateCheckout = () => {
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (data: CheckoutMutation) => createCheckout(data),
    onSuccess: (data) => {
      // Card payments hand off to Stripe. A bank transfer returns
      // { status: 'pending', subscription_id } - the caller shows the
      // "instructions emailed" modal instead.
      if (isCardCheckout(data)) {
        window.location.href = data.checkout_url
      }
    },
    onError: (error) => {
      // The challenge failed here, or the backend rejected its token.
      if (
        error instanceof ChallengeFailedError ||
        (axios.isAxiosError(error) &&
          error.response?.data?.data?.[TURNSTILE_FIELD])
      ) {
        toast.error(t('humanVerificationFailed'))
        return
      }
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.data?.message)
        return
      }
      toast.error(t('somethingWentWrongPleaseTryAgain'))
    },
  })
}

export default useCreateCheckout
